import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import api from '../utils/api';
import { Button, ButtonProps } from '../colaberry/components/core/Button';
import { Badge } from '../colaberry/components/core/Badge';
import { Card } from '../colaberry/components/core/Card';
import { Avatar } from '../colaberry/components/core/Avatar';
import { Progress } from '../colaberry/components/core/Progress';
import { Table } from '../colaberry/components/core/Table';
import { Skeleton } from '../colaberry/components/core/Skeleton';
import { Input } from '../colaberry/components/core/Input';

// SponsorDashboardPage — /sponsor/dashboard (Door B: employer view).
// DS-only, semantic tokens only. Reads GET /api/sponsor/dashboard via the
// axios client with a graceful sample-data fallback so the page always
// renders something credible while the endpoint is wired. The shapes in
// SponsorDashboard / Participant below ARE the contract that endpoint returns.
//
// Auth: magic-link, same email-possession model as the participant portal
// (see backend/src/services/sponsorAuthService.ts). A `?token=` in the URL
// (from the emailed link) is exchanged once for a session and stored in
// localStorage; the dashboard fetch then sends it via the x-sponsor-token
// header. No session yet — including a first-time visitor — sees the sample
// data below plus a small "email me my link" gate rather than a blank page.
//
// Strategy framing: corporate value is TALENT DISCOVERY, not training. The
// copy answers "who are my real AI builders?" — not "did people finish a course?"

// ----- CtaButton: route via href + onClick (DS Button drops RR's `to`) -----
interface CtaButtonProps extends Omit<ButtonProps, 'href' | 'onClick'> {
  to: string;
}
function CtaButton({ to, children, ...rest }: CtaButtonProps) {
  const navigate = useNavigate();
  return (
    <Button
      href={to}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

// ----------------------------- Contract types -----------------------------
type TierName = 'Gold' | 'Silver' | 'Bronze' | 'Unranked';

interface Participant {
  id: string; // stable react key; enrollment_id for live data
  rank: number;
  name: string;
  email: string;
  tier: TierName;
  points: number;
  projects: number;
  certified: boolean;
  demoDayCandidate: boolean;
  avatar?: string;
}

interface SponsorDashboard {
  company: string;
  season: string;
  seatsPurchased: number;
  seatsRedeemed: number;
  seatsAvailable: number;
  seatsReassignable: number; // redeemed seats inactive long enough to reclaim
  tierMax: number; // points ceiling used for the progress fill
  participants: Participant[];
}

// ------------------------------- Provenance -------------------------------
// Every figure on this page says where it came from, beside the figure itself.
//
// WHY PER-FIGURE AND NOT ONE BANNER. This page already told the visitor once,
// in a line near the top, that they were looking at sample data. That is the
// defect the shared Claim primitives were written against: "a dashboard whose
// sample and live modes are visually IDENTICAL except for a badge string, so a
// screenshot of demo data is indistinguishable from a customer's real data."
// A sponsor screenshotting the seat count to forward to their CFO takes the
// number and leaves the banner behind.
//
// WHY NOT IMPORT <Metric> FROM publicV2/Claim. That is the right semantic and
// the wrong stylesheet: it emits cbv2-* classes, and this page ships its own
// scoped <style> block, so the badge would render unstyled and invisible. An
// invisible honesty badge is worse than none, so this mirrors that component's
// rules - text plus glyph, never colour alone - using the design-system Badge
// this page already imports.
type Provenance =
  | 'measured'          // read from our systems for this sponsor
  | 'target'            // a goal agreed with the sponsor, not an observation
  | 'not-yet-measured'  // we will measure it and cannot yet
  | 'sample';           // invented, for a visitor with no session

const PROVENANCE: Record<Provenance, { glyph: string; label: string; tone: BadgeTone }> = {
  measured: { glyph: '✔', label: 'Measured', tone: 'green' },
  target: { glyph: '◎', label: 'Target', tone: 'blue' },
  'not-yet-measured': { glyph: '◷', label: 'Not yet measured', tone: 'neutral' },
  sample: { glyph: '◆', label: 'Sample data', tone: 'warning' },
};

type BadgeTone = 'neutral' | 'red' | 'green' | 'blue' | 'warning';

function Provenance({ of }: { of: Provenance }): React.ReactElement {
  const p = PROVENANCE[of];
  return (
    <div className="cbsd-prov" data-provenance={of}>
      <Badge tone={p.tone} outline>
        <span aria-hidden="true">{p.glyph}</span> {p.label}
      </Badge>
    </div>
  );
}

// ----------------------------- Sample fallback ----------------------------
// Realistic data shown only if the endpoint is unreachable. Mirrors the
// contract exactly so the live view drops in with no shape changes.
// Ids are injected below rather than written on every row — they exist only to
// give React a stable key, and live data keys off enrollment_id instead.
const SAMPLE_PARTICIPANTS: Omit<Participant, 'id'>[] = [
    { rank: 1, name: 'Priya Nandakumar', email: 'priya.n@meridianfreight.com', tier: 'Gold', points: 540, projects: 9, certified: true, demoDayCandidate: true },
    { rank: 2, name: 'Grace Mwangi', email: 'grace.m@meridianfreight.com', tier: 'Gold', points: 505, projects: 8, certified: true, demoDayCandidate: true },
    { rank: 3, name: 'Daniel Okonkwo', email: 'daniel.o@meridianfreight.com', tier: 'Silver', points: 472, projects: 7, certified: true, demoDayCandidate: true },
    { rank: 4, name: 'Hannah Liu', email: 'hannah.l@meridianfreight.com', tier: 'Silver', points: 441, projects: 7, certified: false, demoDayCandidate: false },
    { rank: 5, name: 'Marcus Bell', email: 'marcus.b@meridianfreight.com', tier: 'Silver', points: 398, projects: 6, certified: false, demoDayCandidate: false },
    { rank: 6, name: 'Sofia Alvarez', email: 'sofia.a@meridianfreight.com', tier: 'Silver', points: 352, projects: 5, certified: false, demoDayCandidate: false },
    { rank: 7, name: 'Tyler Brooks', email: 'tyler.b@meridianfreight.com', tier: 'Bronze', points: 244, projects: 4, certified: false, demoDayCandidate: false },
    { rank: 8, name: 'Lena Fischer', email: 'lena.f@meridianfreight.com', tier: 'Bronze', points: 188, projects: 3, certified: false, demoDayCandidate: false },
    { rank: 9, name: 'Omar Haddad', email: 'omar.h@meridianfreight.com', tier: 'Bronze', points: 121, projects: 2, certified: false, demoDayCandidate: false },
    { rank: 10, name: 'Beatriz Santos', email: 'beatriz.s@meridianfreight.com', tier: 'Unranked', points: 40, projects: 1, certified: false, demoDayCandidate: false },
];

// Shown only when the endpoint is unreachable or its payload does not match
// the contract below.
const SAMPLE: SponsorDashboard = {
  company: 'Meridian Freight',
  season: 'Season 3',
  seatsPurchased: 25,
  seatsRedeemed: 19,
  seatsAvailable: 6,
  seatsReassignable: 3,
  tierMax: 600,
  participants: SAMPLE_PARTICIPANTS.map((p, i) => ({ ...p, id: `sample-${i + 1}` })),
};

/* ------------------- API response -> view model ---------------------------
 * GET /api/sponsor/dashboard speaks snake_case and nests its seat counts; this
 * page renders flat camelCase fields. The two were never reconciled, and the
 * only boundary check was `Array.isArray(payload.participants)` — which an
 * empty array passes — so the page accepted a live payload and then rendered
 * `undefined` into the heading and all four stat cards. Map it explicitly, and
 * fall back to sample data when the shape does not match.
 * -------------------------------------------------------------------------*/

interface ApiParticipant {
  rank: number;
  enrollment_id: string;
  full_name: string;
  points: number;
  projects_shipped: number;
  cert_earned: boolean;
  tier: string;
}

interface ApiSponsorDashboard {
  sponsor_id: string;
  company_name: string;
  seats: { purchased: number; redeemed: number; available: number; reassignable: number };
  participants: ApiParticipant[];
  demo_day_candidates: ApiParticipant[];
}

// The API has no notion of a season or a points ceiling; both are presentation
// concerns owned here.
const DEFAULT_SEASON = 'Current season';
const DEFAULT_TIER_MAX = 600;

// Backend tiers are lowercase bronze/silver/gold. This page's tone helpers key
// off capitalised names and know an 'Unranked' state the API never sends, so an
// unmapped value must land somewhere sane rather than fall through as-is.
function toTierName(tier: string): TierName {
  switch ((tier || '').toLowerCase()) {
    case 'gold':
      return 'Gold';
    case 'silver':
      return 'Silver';
    case 'bronze':
      return 'Bronze';
    default:
      return 'Unranked';
  }
}

function isApiSponsorDashboard(payload: unknown): payload is ApiSponsorDashboard {
  const p = payload as Partial<ApiSponsorDashboard> | null;
  return (
    !!p &&
    typeof p.company_name === 'string' &&
    !!p.seats &&
    typeof p.seats.purchased === 'number' &&
    Array.isArray(p.participants)
  );
}

function mapApiSponsorDashboard(payload: unknown): SponsorDashboard | null {
  if (!isApiSponsorDashboard(payload)) return null;

  // Demo-Day membership is the server's judgement (cert earned or work shipped);
  // read it off the list it already sends rather than re-deriving the rule here.
  const demoDayIds = new Set((payload.demo_day_candidates || []).map((c) => c.enrollment_id));

  return {
    company: payload.company_name,
    season: DEFAULT_SEASON,
    seatsPurchased: payload.seats.purchased,
    seatsRedeemed: payload.seats.redeemed,
    seatsAvailable: payload.seats.available,
    seatsReassignable: payload.seats.reassignable,
    tierMax: DEFAULT_TIER_MAX,
    participants: payload.participants.map((p) => ({
      id: p.enrollment_id,
      rank: p.rank,
      name: p.full_name,
      // The endpoint deliberately does not expose participant emails to the
      // sponsor surface; nothing on this page renders one.
      email: '',
      tier: toTierName(p.tier),
      points: p.points,
      projects: p.projects_shipped,
      certified: p.cert_earned,
      demoDayCandidate: demoDayIds.has(p.enrollment_id),
    })),
  };
}

const tierTone = (t: TierName): 'red' | 'neutral' | 'warning' =>
  t === 'Gold' ? 'red' : t === 'Silver' ? 'neutral' : 'warning';

const progressTone = (t: TierName): 'red' | 'blue' | 'green' =>
  t === 'Gold' ? 'red' : t === 'Silver' ? 'blue' : 'green';

// ------------------------------- Styles -----------------------------------
const CSS = `
.cbsd-root{font-family:var(--font-body);color:var(--text-body);background:var(--surface-page);line-height:var(--lh-relaxed);-webkit-font-smoothing:antialiased}
.cbsd-root *{box-sizing:border-box}
.cb-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.cbsd-root h1,.cbsd-root h2,.cbsd-root h3{font-family:var(--font-display);color:var(--text-strong);margin:0;line-height:var(--lh-heading);letter-spacing:var(--ls-tight)}
.cbsd-wrap{max-width:var(--container-lg);margin:0 auto;padding:0 var(--space-6)}
.cbsd-eyebrow{font-size:var(--fs-overline);font-weight:var(--fw-bold);letter-spacing:var(--ls-overline);text-transform:uppercase;color:var(--brand-accent)}

/* HEADER */
.cbsd-head{background:var(--surface-inverse);color:var(--text-on-inverse);padding:var(--space-16) 0 var(--space-12)}
.cbsd-head h1{color:var(--text-on-inverse);font-size:var(--fs-h1);max-width:20ch}
.cbsd-head .cbsd-eyebrow{color:var(--red-300)}
.cbsd-head p{color:var(--neutral-300);max-width:60ch;margin-top:var(--space-4)}
.cbsd-head-row{display:flex;justify-content:space-between;align-items:flex-end;gap:var(--space-8);flex-wrap:wrap}
.cbsd-season-pill{display:inline-flex;align-items:center;gap:var(--space-2);font-size:var(--fs-caption);color:var(--neutral-300);background:rgba(255,255,255,0.06);border:var(--border-1) solid var(--border-default);border-radius:var(--radius-pill);padding:var(--space-2) var(--space-4)}
.cbsd-live{width:8px;height:8px;border-radius:var(--radius-circle);background:var(--status-success);box-shadow:0 0 0 4px color-mix(in srgb, var(--status-success) 30%, transparent)}

/* STAT CARDS */
.cbsd-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:var(--space-5);margin-top:calc(-1 * var(--space-10));position:relative;z-index:1}
.cbsd-stat{padding:var(--space-6)}
.cbsd-stat .label{font-size:var(--fs-caption);font-weight:var(--fw-bold);letter-spacing:var(--ls-overline);text-transform:uppercase;color:var(--text-muted)}
.cbsd-stat .value{font-family:var(--font-display);font-weight:var(--fw-black);font-size:var(--fs-h2);line-height:1;color:var(--text-strong);margin-top:var(--space-3)}
.cbsd-stat .hint{font-size:var(--fs-caption);color:var(--text-subtle);margin-top:var(--space-2)}
.cbsd-prov{margin-top:var(--space-3)}
.cbsd-prov [data-provenance]{display:inline-flex}
.cbsd-stat.accent .value{color:var(--brand-accent)}

/* SECTION HEADERS */
.cbsd-sec{margin:var(--space-12) 0 var(--space-5)}
.cbsd-sec h2{font-size:var(--fs-h3)}
.cbsd-sec p{color:var(--text-muted);max-width:64ch;margin-top:var(--space-2)}

/* DEMO DAY HIGHLIGHT */
.cbsd-dd-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:var(--space-4)}
.cbsd-dd-card{display:flex;align-items:center;gap:var(--space-3);padding:var(--space-5);min-width:0}
.cbsd-dd-card .meta{min-width:0}
.cbsd-dd-card .nm{font-weight:var(--fw-bold);color:var(--text-strong);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cbsd-dd-card .sub{font-size:var(--fs-caption);color:var(--text-muted)}
.cbsd-dd-empty{padding:var(--space-8);text-align:center;color:var(--text-muted)}

/* TABLE CELLS */
.cbsd-rankcell{display:inline-flex;align-items:center;font-family:var(--font-display);font-weight:var(--fw-black);color:var(--text-strong);font-size:var(--fs-body-lg);min-width:var(--space-8)}
.cbsd-builder{display:flex;align-items:center;gap:var(--space-3);min-width:0}
.cbsd-builder .meta{min-width:0}
.cbsd-builder .nm{font-weight:var(--fw-bold);color:var(--text-strong)}
.cbsd-builder .og{font-size:var(--fs-caption);color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cbsd-progcell{min-width:140px;display:inline-block}
.cbsd-ptscell{font-family:var(--font-display);font-weight:var(--fw-black);color:var(--text-strong);font-size:var(--fs-body-lg)}
.cbsd-cert-yes{color:var(--green-700);font-weight:var(--fw-bold)}
.cbsd-cert-no{color:var(--text-subtle)}

/* NOTE + CTA */
.cbsd-note{margin-top:var(--space-6);font-size:var(--fs-caption);color:var(--text-subtle)}
.cbsd-cta{background:var(--surface-subtle);border-radius:var(--radius-xl);padding:var(--space-12);text-align:center;margin:var(--space-16) 0}
.cbsd-cta h2{font-size:var(--fs-h2);max-width:22ch;margin:0 auto var(--space-4)}
.cbsd-cta p{color:var(--text-muted);max-width:54ch;margin:0 auto var(--space-8)}
.cbsd-cta-row{display:flex;gap:var(--space-4);justify-content:center;flex-wrap:wrap}

@media(max-width:980px){.cbsd-stats{grid-template-columns:repeat(2,1fr);margin-top:var(--space-8)}}
@media(max-width:680px){.cbsd-stats{grid-template-columns:1fr}.cbsd-progcell{display:none}}
`;

// ------------------------------ Auth session -------------------------------
const SESSION_KEY = 'cb_sponsor_session';

interface SponsorSession {
  sponsor_id: string;
  access_token: string;
  company_name: string;
}

function loadSponsorSession(): SponsorSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SponsorSession) : null;
  } catch {
    return null;
  }
}

function saveSponsorSession(session: SponsorSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSponsorSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

// --------------------------- Loading skeleton -----------------------------
function StatsSkeleton() {
  return (
    <div className="cbsd-stats" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="cbsd-stat">
          <Skeleton variant="text" width="60%" height={12} />
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Skeleton variant="text" width="40%" height={32} />
          </div>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Skeleton variant="text" width="80%" height={10} />
          </div>
        </Card>
      ))}
    </div>
  );
}

// ------------------------------ Component ---------------------------------
function SponsorDashboardPage() {
  const [data, setData] = useState<SponsorDashboard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [usingSample, setUsingSample] = useState<boolean>(false);

  const [session, setSession] = useState<SponsorSession | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [loginError, setLoginError] = useState('');

  // Resolve auth once on mount: exchange a `?token=` from an emailed link for
  // a session, or fall back to whatever's already stored.
  useEffect(() => {
    let active = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const magic = params.get('token');
      let resolved = loadSponsorSession();
      if (magic) {
        try {
          const res = await api.get<SponsorSession>('/api/sponsor/verify', { params: { token: magic } });
          resolved = res.data;
          saveSponsorSession(resolved);
        } catch {
          resolved = null;
          clearSponsorSession();
        }
        window.history.replaceState({}, '', '/sponsor/dashboard');
      }
      if (!active) return;
      setSession(resolved);
      setAuthChecked(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Load the real dashboard once auth is resolved. No session -> sample data
  // only, no network call (the endpoint requires sponsor_id + a token).
  useEffect(() => {
    if (!authChecked) return;
    if (!session) {
      setData(SAMPLE);
      setUsingSample(true);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .get<unknown>('/api/sponsor/dashboard', {
        params: { sponsor_id: session.sponsor_id },
        headers: { 'x-sponsor-token': session.access_token },
      })
      .then((res) => {
        if (!active) return;
        // Validate and translate the contract at the boundary; fall back if the
        // payload does not match what the endpoint is documented to return.
        const mapped = mapApiSponsorDashboard(res.data);
        if (mapped) {
          setData(mapped);
          setUsingSample(false);
        } else {
          setData(SAMPLE);
          setUsingSample(true);
        }
      })
      .catch((err) => {
        // Graceful degradation — never show an empty page to a sponsor. A
        // rejected/expired token drops the stale session so the login gate
        // reappears instead of retrying it forever.
        if (!active) return;
        if (err?.response?.status === 401) {
          clearSponsorSession();
          setSession(null);
        }
        setData(SAMPLE);
        setUsingSample(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authChecked, session]);

  const requestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim()) {
      setLoginError('Enter your work email.');
      return;
    }
    setLoginError('');
    setLoginSubmitting(true);
    try {
      const res = await api.post<{ message: string }>('/api/sponsor/request-link', {
        email: loginEmail.trim(),
      });
      setLoginMessage(res.data.message || 'Check your email for your login link.');
    } catch (err: any) {
      setLoginError(err?.response?.data?.error || 'Could not send the link. Please try again.');
    } finally {
      setLoginSubmitting(false);
    }
  };

  const view = data ?? SAMPLE;
  const tierMax = view.tierMax || 600;

  const candidates = useMemo(
    () => view.participants.filter((p) => p.demoDayCandidate),
    [view.participants]
  );

  // Named for what the field records, not for what the old copy claimed. The underlying
  // `certified` flag is the API's `cert_earned`, which tracks completion of OUR
  // preparation track; it is not a credential we issue. This count is rendered in prose
  // beside named employees, so the name it carries here is the name the next edit reaches
  // for.
  const prepCompleteCount = useMemo(
    () => view.participants.filter((p) => p.certified).length,
    [view.participants]
  );

  const columns = [
    {
      key: 'rank',
      header: 'Rank',
      render: (v: number) => <span className="cbsd-rankcell">{v}</span>,
    },
    {
      key: 'name',
      header: 'Participant',
      render: (_v: string, row: Participant) => (
        <span className="cbsd-builder">
          <Avatar name={row.name} src={row.avatar} size="sm" ring={row.tier === 'Gold'} />
          <span className="meta cb-min0">
            <span className="nm">{row.name}</span>
            <span className="og">{row.email}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (_v: TierName, row: Participant) =>
        row.tier === 'Unranked' ? (
          <Badge tone="neutral">Unranked</Badge>
        ) : (
          <Badge tone={tierTone(row.tier)} dot>
            {row.tier}
          </Badge>
        ),
    },
    {
      key: 'points',
      header: 'Points',
      render: (_v: number, row: Participant) => (
        <span
          className="cbsd-progcell"
          aria-label={`${row.points} of ${tierMax} points toward next tier`}
        >
          <Progress value={row.points} max={tierMax} tone={progressTone(row.tier)} />
        </span>
      ),
    },
    {
      key: 'projects',
      header: 'Projects',
      align: 'right' as const,
      render: (v: number) => <span title={`${v} shipped`}>{v}</span>,
    },
    {
      // WE DO NOT CERTIFY ANYONE, and this column used to say we did. It read
      // "Cert", with the states "Certified" and "Not yet certified", which claims
      // a credential Colaberry does not issue: the exam belongs to Anthropic and
      // we sell preparation for it. A sponsor reading "Certified" beside their own
      // employee's name would reasonably believe we had awarded something. What
      // the underlying field records is whether that person finished our
      // preparation track, so that is what it now says.
      key: 'certified',
      header: 'Prep',
      align: 'right' as const,
      render: (_v: boolean, row: Participant) =>
        row.certified ? (
          <span className="cbsd-cert-yes" title="Preparation track complete">
            <span aria-hidden="true">✓</span>
            <span className="cb-sr-only">Preparation complete</span>
          </span>
        ) : (
          <span className="cbsd-cert-no" title="Preparation track not yet complete">
            <span aria-hidden="true">—</span>
            <span className="cb-sr-only">Preparation not yet complete</span>
          </span>
        ),
    },
  ];

  return (
    <div className="cbsd-root">
      <style>{CSS}</style>
      <SEOHead
        title="Sponsor Dashboard"
        description="See who your real AI builders are. Track seats, tiers, shipped projects, and Demo Day candidates across your sponsored team — without taking anyone off the job."
      />

      {/* HEADER */}
      <header
        className="cbsd-head"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--surface-inverse)',
          color: 'var(--text-on-inverse)',
          padding: 'var(--space-24) var(--space-5) var(--space-16)',
        }}
      >
        {/* Hero photo + dark overlay (decorative) */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage:
              "linear-gradient(180deg, color-mix(in srgb, var(--surface-inverse) 74%, transparent), color-mix(in srgb, var(--surface-inverse) 93%, transparent)), url('/img/online-presentation.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="cbsd-wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="cbsd-head-row">
            <div>
              <div className="cbsd-eyebrow" style={{ color: 'var(--text-on-inverse)', opacity: 0.85 }}>
                Sponsor Dashboard
              </div>
              <h1 className="cb-balance" style={{ marginTop: 'var(--space-3)', color: 'var(--text-on-inverse)' }}>
                {view.company}: find out who your real AI builders are.
              </h1>
              <p style={{ color: 'var(--text-on-inverse)', opacity: 0.88 }}>
                Every seat you sponsor puts a colleague in the same Challenge as everyone else — they learn on
                their own time and ship real projects. This is where you watch the talent surface, without taking
                anyone off the job.
              </p>
            </div>
            <span className="cbsd-season-pill">
              <span className="cbsd-live" aria-hidden /> {view.season} · Live
            </span>
          </div>
        </div>
      </header>

      {/* LOGIN GATE — shown until a magic-link session is established */}
      {authChecked && !session && (
        <section className="cbsd-wrap" style={{ marginTop: 'var(--space-6)' }} aria-label="Sponsor login">
          <Card elevation="sm" style={{ padding: 'var(--space-6)', maxWidth: 520 }}>
            {loginMessage ? (
              <p style={{ margin: 0 }}>{loginMessage}</p>
            ) : (
              <form onSubmit={requestLink} noValidate>
                <div className="row g-2 align-items-end">
                  <div className="col-sm-8">
                    <Input
                      label="Work email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      error={loginError}
                      placeholder="you@yourcompany.com"
                      autoComplete="email"
                    />
                  </div>
                  <div className="col-sm-4 d-grid">
                    <Button type="submit" disabled={loginSubmitting}>
                      {loginSubmitting ? 'Sending…' : 'Email me my link'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-subtle)', marginTop: 'var(--space-3)', marginBottom: 0 }}>
              Below is sample data so you can see what your dashboard looks like once you're signed in.
            </p>
          </Card>
        </section>
      )}

      {/* SEAT STAT CARDS */}
      <section className="cbsd-wrap" aria-label="Seat usage">
        {loading ? (
          <StatsSkeleton />
        ) : (
          <div className="cbsd-stats">
            <Card className="cbsd-stat" elevation="sm">
              <div className="label">Seats purchased</div>
              <div className="value">{view.seatsPurchased}</div>
              <div className="hint">Annual seats on your plan</div>
              <Provenance of={usingSample ? 'sample' : 'measured'} />
            </Card>
            <Card className="cbsd-stat" elevation="sm">
              <div className="label">Seats redeemed</div>
              <div className="value">{view.seatsRedeemed}</div>
              <div className="hint">Codes claimed by your team</div>
              <Provenance of={usingSample ? 'sample' : 'measured'} />
            </Card>
            <Card className="cbsd-stat" elevation="sm" accent="green">
              <div className="label">Available</div>
              <div className="value">{view.seatsAvailable}</div>
              <div className="hint">Unassigned — ready to invite</div>
              <Provenance of={usingSample ? 'sample' : 'measured'} />
            </Card>
            <Card className="cbsd-stat accent" elevation="sm" accent="red">
              <div className="label">Reassignable</div>
              <div className="value">{view.seatsReassignable}</div>
              <div className="hint">Inactive seats you can reclaim</div>
              <Provenance of={usingSample ? 'sample' : 'measured'} />
            </Card>
          </div>
        )}
      </section>

      {/* DEMO DAY CANDIDATES */}
      <section className="cbsd-wrap">
        <div className="cbsd-sec">
          <Badge solid>Demo Day candidates</Badge>
          <h2 style={{ marginTop: 'var(--space-3)' }}>Your standouts, ready to present.</h2>
          <p>
            These builders have shipped enough, climbed high enough, and finished the preparation track — they are
            the people to put on stage at Demo Day. This is talent discovery in action: proof of who can actually
            build with AI.
          </p>
        </div>

        {loading ? (
          <div className="cbsd-dd-grid" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="cbsd-dd-card">
                <Skeleton variant="circle" width={40} height={40} />
                <span className="meta cb-min0" style={{ flex: 1 }}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="45%" height={10} />
                </span>
              </Card>
            ))}
          </div>
        ) : candidates.length > 0 ? (
          <div className="cbsd-dd-grid">
            {candidates.map((p) => (
              <Card key={p.id} className="cbsd-dd-card" elevation="sm" hoverable accent="red">
                <Avatar name={p.name} src={p.avatar} size="md" ring />
                <span className="meta cb-min0">
                  <span className="nm">{p.name}</span>
                  <span className="sub">
                    {p.points.toLocaleString()} pts · {p.projects} {p.projects === 1 ? 'project' : 'projects'}
                  </span>
                </span>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="cbsd-dd-empty" elevation="sm">
            No Demo Day candidates yet. As your team ships projects and finishes the preparation track, standouts
            will surface here.
          </Card>
        )}
      </section>

      {/* PARTICIPANTS TABLE */}
      <section className="cbsd-wrap" style={{ paddingBottom: 'var(--space-12)' }}>
        <div className="cbsd-sec">
          <h2>Your sponsored team</h2>
          <p>
            {prepCompleteCount} of {view.participants.length} active builders have completed the preparation
            track so far. Ranked by points earned in {view.season}. Points come from shipping, not watching.
          </p>
        </div>

        <Table columns={columns} data={view.participants} hover striped />

        <p className="cbsd-note">
          Company-scoped view — you see only builders on your purchased seats.{' '}
          {usingSample ? (
            <>Showing sample data; this view reads <code>GET /api/sponsor/dashboard</code> once the endpoint is live.</>
          ) : (
            <>Live from <code>GET /api/sponsor/dashboard</code>.</>
          )}
        </p>
      </section>

      {/* CTA */}
      <section className="cbsd-wrap">
        <div className="cbsd-cta">
          <Badge solid>Seats left to fill?</Badge>
          <h2 style={{ marginTop: 'var(--space-4)' }}>
            Every unassigned seat is a builder you haven&apos;t discovered yet.
          </h2>
          <p>
            Invite more of your team into the Challenge, or top up your plan. The more people you put in, the
            clearer the picture of who your real AI builders are.
          </p>
          <div className="cbsd-cta-row">
            <CtaButton to="/sponsorship" size="lg" trailingIcon={<span aria-hidden>→</span>}>
              Sponsor more seats
            </CtaButton>
            <CtaButton to="/leaderboard" size="lg" variant="outline">
              View the leaderboard
            </CtaButton>
            <CtaButton to="/demo-day" size="lg" variant="ghost">
              About Demo Day
            </CtaButton>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SponsorDashboardPage;
